import React, { useEffect, useState } from 'react';
import { Box, Button, Chip, CircularProgress, Dialog, DialogContent, DialogTitle, IconButton, TextField, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Visitor } from './VisitRequestStatus';
import { getVisitorIdSecurityCardNumberHistoryAPI } from 'apis/visitRequest/visitorsAPI';
import dayjs, { Dayjs } from 'dayjs';
import { VISITOR_SECURITY_CARD_STATUS } from '../../constants/constants';
import { useTranslation } from 'react-i18next';
import SnackbarAlert from 'components/SnackbarAlert';
import { useSnackbarStore } from 'store/useSnackbarStore';

type VisitorSecurityCardStatus = 'NONE' | 'ASSIGNED' | 'ISSUED' | 'RETURNED' | 'LOST' | 'DAMAGED' | 'LOSE_CANCELED' | 'DAMAGE_CANCELED';

interface VisitRequestStatusPopupProps {
  isOpen: boolean;
  selectedDate: Dayjs;
  handleClose: () => void;
  securityCardData: Visitor | null; // securityCardData가 null일 수 있으므로 null 허용
  onSecurityCardStateChange: (
    visitorId: number,
    securityCardNumber: string,
    action: 'ISSUE' | 'RETURN' | 'DAMAGE' | 'LOSE' | 'LOSE_CANCELED' | 'DAMAGE_CANCELED'
  ) => Promise<any>;
}

export default function VisitRequestStatusPopup({
  isOpen,
  selectedDate,
  handleClose,
  securityCardData,
  onSecurityCardStateChange
}: VisitRequestStatusPopupProps) {
  const [securityCardHistories, setSecurityCardHistories] = useState<any[]>([]); // 방문내역 조회 API 결과
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState<'DAMAGE' | 'LOSE' | null>(null);
  const [cardNumberInput, setCardNumberInput] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  // 다크 라이트 모드
  const theme = useTheme();
  // 다국어
  const { t } = useTranslation();
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar); //Alert zustand 상태 관리

  const todayStr = dayjs().format('YYYY-MM-DD');

  // 출입보안카드 팝업 닫을때 핸들러
  const handleCloseAndReset = () => {
    setSecurityCardHistories([]); // 방문내역 초기화
    handleClose(); // 모달을 닫기 위해 부모로부터 받은 handleClose 호출
  };

  // 출입보안카드 팝업 속 (회수, 파손, 분실)버튼 클릭시 호출되는 핸들러
  const handleAction = (
    visitorId: number,
    securityCardNumber: string,
    action: 'ISSUE' | 'RETURN' | 'DAMAGE' | 'LOSE' | 'LOSE_CANCELED' | 'DAMAGE_CANCELED'
  ) => {
    // 상태 변경 함수 호출
    // 파손, 분실의 경우 한번 더 컨펌모달
    if (action === 'DAMAGE' || action === 'LOSE') {
      setConfirmAction(action);
      setConfirmDialogOpen(true);
    } else {
      const result = onSecurityCardStateChange(visitorId, securityCardNumber, action);
      result.catch((err) => {
        showSnackbar(err?.data?.message || t('visit_request_status.popup.message.error'), 'error');
      });
    }
  };

  // 확인버튼 클릭하면 호출됨 (입력한 카드번호와 카드번호가 일치하면 호출 => 관리번호가 일치하는 것으로 변경, 아니면 에러메세지)
  const handleConfirmAction = () => {
    if (
      confirmAction &&
      securityCardData?.securityCardNumber &&
      securityCardData?.securityManageNumber &&
      securityCardData?.securityManageNumber === cardNumberInput
    ) {
      // api에 보낼 때는 내부에서 관리하는 카드번호 전달
      const result = onSecurityCardStateChange(securityCardData.id, securityCardData.securityCardNumber, confirmAction!);
      result
        .then((res) => {
          setConfirmDialogOpen(false);
          setConfirmAction(null);
          setCardNumberInput('');
        })
        .catch((err) => {
          showSnackbar(err?.data?.message || t('visit_request_status.popup.message.error'), 'error');
        });
    } else {
      setError(t('visit_request_status.popup.message.card_number_wrong')); // 카드번호가 일치하지 않습니다
    }
  };

  // 카드번호가 있을때, getVisitorIdSecurityCardNumberHistoryAPI 함수 호출하는 함수
  const fetchSecurityCardHistory = async () => {
    // 카드번호가 있을경우만 API 호출
    if (securityCardData?.securityCardNumber) {
      setIsLoading(true);
      const data = await getVisitorIdSecurityCardNumberHistoryAPI(securityCardData.id, securityCardData.securityCardNumber);
      // 데이터가 객체라면 배열로 변환, 배열이라면 그대로 설정
      setSecurityCardHistories(Array.isArray(data.data) ? data.data : [data.data]);
      setIsLoading(false);
    }
  };

  // 클릭한 securityCardData가 달라지면 출입보안카드 내역 호출
  useEffect(() => {
    fetchSecurityCardHistory();
  }, [securityCardData]);

  return (
    <>
      <SnackbarAlert />
      <Dialog
        open={isOpen}
        onClose={handleCloseAndReset}
        fullWidth
        maxWidth="sm" // 최대 너비 설정 (sm, md, lg 등 옵션 사용 가능)
        sx={{
          margin: 'auto', // 중앙 정렬
          '& .MuiDialog-paper': {
            width: '90%', // 화면 크기에 따라 너비를 동적으로 설정
            maxWidth: '600px', // 최대 너비 제한
            [theme.breakpoints.down('sm')]: {
              width: '95%', // 작은 화면에서는 더 좁게
              maxWidth: '400px' // 모바일에서는 최대 400px로 제한
            }
          }
        }}
      >
        <DialogTitle className="headerTitle" sx={{ bgcolor: theme.palette.table.header, border: 'none' }}>
          <span>{t('visit_request_status.popup.access_security_card_info')}</span>
          <IconButton onClick={handleCloseAndReset} sx={{ marginLeft: 'auto' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            bgcolor: theme.palette.mode === 'dark' ? theme.palette.contentBlock.background : theme.palette.contentBlock.main,
            paddingY: '20px !important',
            borderRadius: 1,
            [theme.breakpoints.down('sm')]: {
              paddingY: '16px' // 모바일에서는 상하 간격 줄이기
            }
          }}
        >
          <Box
            borderRadius={1}
            bgcolor={theme.palette.mode == 'dark' ? theme.palette.contentBlock.main : theme.palette.contentBlock.background}
            padding={2}
            display="flex"
            flexDirection="column"
            gap={1}
            sx={{ marginBottom: '20px' }}
          >
            <Typography variant="h6" fontWeight="bold">
              {securityCardData?.name}
            </Typography>
            <Box
              borderRadius={1}
              bgcolor={theme.palette.mode == 'dark' ? theme.palette.contentBlock.background : theme.palette.contentBlock.main}
              boxShadow={1}
              padding={2}
              display="flex"
              flexDirection="column"
              gap={1}
            >
              <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                <Box display="flex" flexDirection="row" alignItems="center" gap={1} width="100%">
                  <Typography variant="h6" color="primary.dark" fontWeight="bold">
                    {securityCardData?.securityManageNumber}
                  </Typography>
                  <Chip
                    label={`${t('visit_request_status.popup.status')} : ${t(VISITOR_SECURITY_CARD_STATUS[securityCardData?.securityCardStatus || 'NONE']?.name)} `}
                    variant="outlined"
                    size="small"
                    sx={{ fontSize: '12px', userSelect: 'none' }}
                  />
                  <Box flexGrow={1} />
                  {/* 발금처리 */}
                  {(selectedDate.isSame(dayjs(), 'day') || selectedDate.isAfter(dayjs(), 'day')) &&
                    (securityCardData?.securityCardStatus === 'ASSIGNED' ||
                      (securityCardData?.securityCardStatus === 'RETURNED' && dayjs().isAfter(securityCardData?.securityCardDate))) && (
                      <Button
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          if (securityCardData?.securityCardNumber) {
                            handleAction(securityCardData.id, securityCardData.securityCardNumber, 'ISSUE');
                          }
                          // else {
                          //   console.log('Security card number is missing');
                          // }
                        }}
                        sx={{ borderRadius: '4px', bgcolor: theme.palette.fieldText.background }}
                      >
                        {t('visit_request_status.popup.assigned')}
                      </Button>
                    )}
                  {/* 분실취소 */}
                  {securityCardData?.securityCardStatus === 'LOST' && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        if (securityCardData?.securityCardNumber) {
                          handleAction(securityCardData.id, securityCardData.securityCardNumber, 'LOSE_CANCELED');
                        }
                        // else {
                        //   console.log('분실취소함');
                        // }
                      }}
                      sx={{ borderRadius: '4px', bgcolor: theme.palette.fieldText.background }}
                    >
                      {t('visit_request_status.popup.lose_cancel')}
                    </Button>
                  )}
                  {/* 파손취소 */}
                  {securityCardData?.securityCardStatus === 'DAMAGED' && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={() => {
                        if (securityCardData?.securityCardNumber) {
                          handleAction(securityCardData.id, securityCardData.securityCardNumber, 'DAMAGE_CANCELED');
                        }
                        // else {
                        //   console.log('분실취소함');
                        // }
                      }}
                      sx={{ borderRadius: '4px', bgcolor: theme.palette.fieldText.background }}
                    >
                      {t('visit_request_status.popup.damage_cancel')}
                    </Button>
                  )}
                </Box>
                {/* 회수처리 */}
                {(securityCardData?.securityCardStatus === 'ISSUED' ||
                  (securityCardData?.securityCardStatus === 'ASSIGNED' &&
                    (todayStr < securityCardData?.visitRequest?.visitFrom || securityCardData?.visitRequest?.visitTo < todayStr))) && (
                  <Box display="flex" flexDirection="row" alignItems="center" gap={1} width="100%">
                    <Button
                      variant="outlined"
                      color="primary"
                      onClick={() => {
                        if (securityCardData?.securityCardNumber) {
                          handleAction(securityCardData.id, securityCardData.securityCardNumber, 'RETURN');
                        }
                        // else {
                        //   console.log('Security card number is missing');
                        // }
                      }}
                      sx={{ borderRadius: '4px', bgcolor: theme.palette.fieldText.background }}
                    >
                      {t('visit_request_status.popup.return')}
                    </Button>
                    {/* 파손처리 */}
                    <Button
                      variant="outlined"
                      color="warning"
                      onClick={() => {
                        if (securityCardData?.securityCardNumber) {
                          handleAction(securityCardData.id, securityCardData.securityCardNumber, 'DAMAGE');
                        }
                        // else {
                        //   console.log('Security card number is missing');
                        // }
                      }}
                      sx={{ borderRadius: '4px', bgcolor: theme.palette.fieldText.background }}
                    >
                      {t('visit_request_status.popup.damage')}
                    </Button>
                    {/* 분실처리 */}
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => {
                        if (securityCardData?.securityCardNumber) {
                          handleAction(securityCardData.id, securityCardData.securityCardNumber, 'LOSE');
                        }
                        // else {
                        //   console.log('Security card number is missing');
                        // }
                      }}
                      sx={{ borderRadius: '4px', bgcolor: theme.palette.fieldText.background }}
                    >
                      {t('visit_request_status.popup.lost')}
                    </Button>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          {/* 내역 */}
          <Box
            borderRadius={1}
            bgcolor={theme.palette.mode == 'dark' ? theme.palette.contentBlock.main : theme.palette.contentBlock.background}
            padding={2}
            display="flex"
            flexDirection="column"
            gap={1}
          >
            <Typography variant="h6" fontWeight="bold">
              {t('visit_request_status.popup.list')}
            </Typography>
            <Box
              borderRadius={1}
              bgcolor="#ffff"
              boxShadow={1}
              padding={2}
              display="flex"
              flexDirection="column"
              gap={1}
              sx={{
                maxHeight: '200px',
                overflowY: 'auto',
                bgcolor: theme.palette.mode == 'dark' ? theme.palette.contentBlock.background : theme.palette.contentBlock.main
              }}
            >
              {isLoading ? (
                <Box display="flex" alignItems="center" justifyContent="center">
                  <CircularProgress size="small" />
                </Box>
              ) : securityCardHistories.length > 0 ? (
                securityCardHistories
                  .slice()
                  .reverse()
                  .map((securityCardHistory) => (
                    <Box key={securityCardHistory.createdAt} display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" gap={1}>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        {dayjs(securityCardHistory.createdAt).format('YYYY-MM-DD HH:mm:ss')}
                      </Box>
                      <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                        {t(VISITOR_SECURITY_CARD_STATUS[securityCardHistory.status as VisitorSecurityCardStatus]?.name) || ''}
                      </Box>
                    </Box>
                  ))
              ) : (
                // 내역이 없습니다.
                <Box display="flex" flexDirection="row" alignItems="center" justifyContent="center">
                  {t('visit_request_status.popup.no_list')}
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      {/* 파손, 분실 컨펌 모달 = DialogTitle을 할지, DialogContent를 할지 모르겠어서 우선 주석처리해놓음 */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
        <DialogTitle>
          {t('visit_request_status.popup.dialog.really')} {confirmAction === 'DAMAGE' ? '파손' : '분실'} {t('visit_request_status.popup.dialog.do_it')}
        </DialogTitle>
        <DialogContent>
          <main style={{ margin: '10px auto' }}>
            {/* 카드번호를 입력해주세요 */}
            <Typography sx={{ marginBottom: 1 }}>{t('visit_request_status.popup.dialog.enter_card_number')} </Typography>
            {/* 카드번호 입력창 */}
            <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
              <Typography width={80} variant="caption" color={theme.palette.fieldText.title}>
                {t('visit_request_status.popup.dialog.card_number')}
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                value={cardNumberInput}
                onChange={(e) => {
                  setCardNumberInput(e.target.value);
                  setError('');
                }}
                error={!!error}
                helperText={error}
              />
            </Box>
          </main>

          <Box display="flex" justifyContent="flex-end" gap={1} marginTop={2}>
            {/* 취소 */}
            <Button variant="contained" color="error" onClick={() => setConfirmDialogOpen(false)} sx={{ borderRadius: '4px' }}>
              {t('visit_request_status.popup.dialog.cancel')}
            </Button>
            {/* 확인 */}
            <Button variant="contained" color="primary" onClick={handleConfirmAction} sx={{ borderRadius: '4px' }}>
              {t('visit_request_status.popup.dialog.ok')}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </>
  );
}
